import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useShortcuts } from '@/components/shortcuts';

import { onPlatformSignedOut, platformRefresh, platformSignOut, type PlatformUser } from './client';
import { CompaniesPage } from './CompaniesPage';
import { CompanyPage } from './CompanyPage';
import { NewCompanyPage } from './NewCompanyPage';
import { PackagesPage } from './PackagesPage';
import { PaymentPage } from './PaymentPage';
import { PlanPricesPage } from './PlanPricesPage';
import { PlatformSignIn } from './PlatformSignIn';
import { ReceiptPage } from './ReceiptPage';
import { TermsPage } from './TermsPage';

type View =
  | { kind: 'companies' }
  | { kind: 'new' }
  | { kind: 'company'; tenantId: number }
  | { kind: 'terms'; tenantId: number }
  | { kind: 'payment'; tenantId: number }
  | { kind: 'receipt'; tenantId: number; paymentId: number }
  | { kind: 'packages' }
  | { kind: 'prices'; planCode: string };

/** The two places the header goes; everything else is reached from one of them. */
const sections = [
  { kind: 'companies', label: 'Companies', icon: 'ph-buildings' },
  { kind: 'packages', label: 'Packages', icon: 'ph-package' },
] as const;

const sectionOf = (view: View): 'companies' | 'packages' =>
  view.kind === 'packages' || view.kind === 'prices' ? 'packages' : 'companies';

/**
 * The platform panel, at `/platform` on the shop's own origin — a separate root, not a page in the
 * shop's shell. The shell is built from a shop session (its menu, its branch, its permissions), and
 * none of that exists for the person running the platform. Sub-screens are internal state, as the
 * shop's are, because there is no router.
 */
export function PlatformApp() {
  useShortcuts();

  const [user, setUser] = useState<PlatformUser | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [view, setView] = useState<View>({ kind: 'companies' });
  const queryClient = useQueryClient();

  // The cache is keyed by URL, not by account: kept across a sign-out, the next person to sign in
  // on this browser would start on the last one's screens.
  useEffect(() => {
    onPlatformSignedOut(() => {
      queryClient.clear();
      setUser(null);
    });
    // An open tab survives a reload through the platform refresh cookie, exactly as a shop's does.
    void platformRefresh().then((auth) => {
      if (auth && !auth.user.mustChangePassword) setUser(auth.user);
      setRestoring(false);
    });
    return () => onPlatformSignedOut(null);
  }, [queryClient]);

  if (restoring) return null;

  if (!user) {
    return (
      <PlatformSignIn
        onSignedIn={(auth) => {
          setUser(auth.user);
          setView({ kind: 'companies' });
        }}
      />
    );
  }

  const signOut = () =>
    void platformSignOut().finally(() => {
      queryClient.clear();
      setUser(null);
    });

  return (
    <div className="bg-light min-h-screen">
      <header className="border-border-color flex items-center justify-between border-b bg-white px-4 py-3">
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-900"
            onClick={() => setView({ kind: 'companies' })}
          >
            <i className="ph-duotone ph-buildings text-primary text-[20px]" />
            <span className="max-sm:hidden">AttireRent platform</span>
          </button>
          <nav className="flex items-center gap-1" aria-label="Platform">
            {sections.map((s) => {
              const current = sectionOf(view) === s.kind;
              return (
                <button
                  key={s.kind}
                  type="button"
                  aria-current={current ? 'page' : undefined}
                  className={`flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1.5 text-sm ${
                    current
                      ? 'bg-light font-semibold text-gray-900'
                      : 'text-default hover:text-gray-900'
                  }`}
                  onClick={() => setView({ kind: s.kind })}
                >
                  <i className={`ph ${s.icon} text-[16px]`} />
                  {s.label}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-default text-[length:var(--scale-field-hint)] max-sm:hidden">
            {user.fullName} · {user.role}
          </span>
          <button
            type="button"
            className="text-default hover:text-gray-900 cursor-pointer text-sm"
            onClick={signOut}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl p-3 lg:p-6">
        {view.kind === 'companies' && (
          <CompaniesPage
            canWrite={user.role === 'SuperAdmin'}
            onNew={() => setView({ kind: 'new' })}
            onOpen={(tenantId) => setView({ kind: 'company', tenantId })}
          />
        )}
        {view.kind === 'new' && (
          <NewCompanyPage
            onClose={() => setView({ kind: 'companies' })}
            onCreated={(tenantId) => setView({ kind: 'company', tenantId })}
          />
        )}
        {view.kind === 'company' && (
          <CompanyPage
            tenantId={view.tenantId}
            canWrite={user.role === 'SuperAdmin'}
            onBack={() => setView({ kind: 'companies' })}
            onSetTerms={() => setView({ kind: 'terms', tenantId: view.tenantId })}
            onRecordPayment={() => setView({ kind: 'payment', tenantId: view.tenantId })}
            onReceipt={(paymentId) =>
              setView({ kind: 'receipt', tenantId: view.tenantId, paymentId })
            }
          />
        )}
        {view.kind === 'terms' && (
          <TermsPage
            tenantId={view.tenantId}
            onBack={() => setView({ kind: 'company', tenantId: view.tenantId })}
          />
        )}
        {view.kind === 'payment' && (
          <PaymentPage
            tenantId={view.tenantId}
            onBack={() => setView({ kind: 'company', tenantId: view.tenantId })}
            onRecorded={(paymentId) =>
              setView({ kind: 'receipt', tenantId: view.tenantId, paymentId })
            }
          />
        )}
        {view.kind === 'receipt' && (
          <ReceiptPage
            tenantId={view.tenantId}
            paymentId={view.paymentId}
            onBack={() => setView({ kind: 'company', tenantId: view.tenantId })}
          />
        )}
        {view.kind === 'packages' && (
          <PackagesPage
            canWrite={user.role === 'SuperAdmin'}
            onEdit={(planCode) => setView({ kind: 'prices', planCode })}
          />
        )}
        {view.kind === 'prices' && (
          <PlanPricesPage planCode={view.planCode} onBack={() => setView({ kind: 'packages' })} />
        )}
      </main>
    </div>
  );
}
